import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ResourceTeam, ResourceTeamSave } from '../../../Models/resource-modules.models';
import { ResourceTeamManagementStore } from './resource-team-management.store';
import { ResourceAccessService } from '../../../Services/resource-access.service';
import {
  ResourceTeamFormDialogComponent,
  ResourceTeamFormDialogData
} from './team-form-dialog/resource-team-form-dialog.component';

type ManagementView = 'teams' | 'mitarbeiter';

/**
 * Team- und Mitarbeiterverwaltung: Teamansicht (Liste + Details) und „Alle
 * Mitarbeiter“, dazu das Mitarbeiterprofil als Seitenpanel. Ansicht, Team und
 * Profil stehen in der URL (?ansicht, ?team, ?mitarbeiter), damit Links und
 * Weiterleitungen der alten Seiten „Teams“ und „Mitarbeiter“ funktionieren.
 */
@Component({
  selector: 'app-resource-team-management',
  templateUrl: './resource-team-management.component.html',
  styleUrls: ['./resource-team-management.shared.css', './resource-team-management.component.css'],
  providers: [ResourceTeamManagementStore]
})
export class ResourceTeamManagementComponent implements OnInit {
  view: ManagementView = 'teams';
  selectedTeamId: number | null = null;
  profileUserId: string | null = null;
  teamSearch = '';

  constructor(
    public store: ResourceTeamManagementStore,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.view = params.get('ansicht') === 'mitarbeiter' ? 'mitarbeiter' : 'teams';
    const team = Number(params.get('team'));
    this.selectedTeamId = Number.isFinite(team) && team > 0 ? team : null;
    this.profileUserId = params.get('mitarbeiter');

    this.store.load().subscribe(() => {
      if (this.view === 'mitarbeiter' && !this.store.canSeeDirectory) this.view = 'teams';
      if (this.selectedTeamId !== null && !this.store.team(this.selectedTeamId)) this.selectedTeamId = null;
      // Ohne Auswahl: das erste eigene Team öffnen (bzw. das erste sichtbare).
      if (this.selectedTeamId === null && this.view === 'teams') {
        this.selectedTeamId = this.defaultTeamId();
      }
      if (this.profileUserId && !this.store.profile(this.profileUserId)) this.profileUserId = null;
      this.syncUrl();
    });
  }

  // ---- Teamliste -----------------------------------------------------------------

  get selectedTeam(): ResourceTeam | undefined {
    return this.store.team(this.selectedTeamId);
  }

  private matches(team: ResourceTeam): boolean {
    const q = this.teamSearch.trim().toLocaleLowerCase('de');
    return !q || team.name.toLocaleLowerCase('de').includes(q) || (team.leaderName ?? '').toLocaleLowerCase('de').includes(q);
  }

  isMember(team: ResourceTeam): boolean {
    return team.members.some(m => m.userId === this.store.currentUserId);
  }

  get myTeams(): ResourceTeam[] {
    return this.store.teams.filter(t => this.isMember(t) && this.matches(t));
  }

  get otherTeams(): ResourceTeam[] {
    return this.store.teams.filter(t => !this.isMember(t) && this.matches(t));
  }

  myRole(team: ResourceTeam): { leader: boolean; primary: boolean } {
    const me = this.store.access?.teams.find(t => t.teamId === team.id);
    return { leader: !!me?.isLeader, primary: !!me?.isPrimaryTeam && (this.store.access?.teams.length ?? 0) > 1 };
  }

  /** Eigener Hinweis: mehrere Teams, aber kein Hauptteam gewählt. */
  get myPrimaryMissing(): boolean {
    const teams = this.store.access?.teams ?? [];
    return teams.length > 1 && !teams.some(t => t.isPrimaryTeam);
  }

  trackTeam(_i: number, team: ResourceTeam): number { return team.id; }

  /**
   * Startauswahl nach gemeinsamer Regel (eigenes Hauptteam, sonst geleitetes bzw.
   * eigenes Team); ohne eigenes Team das erste sichtbare. Nur verwenden, wenn noch
   * kein Team gewählt ist – eine bestehende Auswahl wird nie überschrieben.
   */
  private defaultTeamId(): number | null {
    const activeTeamIds = this.store.teams.filter(t => t.isActive).map(t => t.id);
    return ResourceAccessService.defaultTeamId(this.store.access, activeTeamIds, false)
      ?? this.myTeams[0]?.id ?? this.otherTeams[0]?.id ?? null;
  }

  // ---- Navigation in der Seite -------------------------------------------------------

  setView(view: ManagementView): void {
    this.view = view;
    if (view === 'teams' && this.selectedTeamId === null) {
      this.selectedTeamId = this.defaultTeamId();
    }
    this.syncUrl();
  }

  selectTeam(teamId: number): void {
    this.selectedTeamId = teamId;
    this.view = 'teams';
    this.syncUrl();
  }

  /** Scrollbereich des Profilpanels (bleibt beim Wechsel zwischen Mitarbeitern bestehen). */
  @ViewChild('profileScroll') private profileScroll?: ElementRef<HTMLElement>;

  openProfile(userId: string): void {
    const switched = this.profileUserId !== userId;
    this.profileUserId = userId;
    this.syncUrl();
    // Ein anderer Mitarbeiter beginnt immer oben.
    if (switched) this.profileScroll?.nativeElement.scrollTo({ top: 0 });
  }

  closeProfile(): void {
    this.profileUserId = null;
    this.syncUrl();
  }

  /** Aus dem Profil zu einem Team springen. */
  showTeam(teamId: number): void {
    this.profileUserId = null;
    this.selectTeam(teamId);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.profileUserId && !this.dialog.openDialogs.length) this.closeProfile();
  }

  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      replaceUrl: true,
      queryParams: {
        ansicht: this.view === 'mitarbeiter' ? 'mitarbeiter' : null,
        team: this.view === 'teams' ? this.selectedTeamId : null,
        mitarbeiter: this.profileUserId
      }
    });
  }

  // ---- Team anlegen / bearbeiten -----------------------------------------------------

  createTeam(): void {
    this.openTeamDialog(null);
  }

  editTeam(team: ResourceTeam): void {
    this.openTeamDialog(team);
  }

  private openTeamDialog(team: ResourceTeam | null): void {
    const data: ResourceTeamFormDialogData = {
      team,
      userOptions: this.store.userOptions,
      currentUserId: this.store.currentUserId,
      canChooseLeader: this.store.isGlobal
    };
    this.dialog.open<ResourceTeamFormDialogComponent, ResourceTeamFormDialogData, ResourceTeamSave>(
      ResourceTeamFormDialogComponent, { data, width: '560px', maxWidth: '95vw', autoFocus: 'first-tabbable' })
      .afterClosed()
      .subscribe(payload => {
        if (!payload) return;
        const request = team ? this.store.updateTeam(team.id, payload) : this.store.createTeam(payload);
        request.subscribe({
          next: saved => {
            this.snackBar.open(team ? 'Änderungen gespeichert.' : `Team „${saved.name}“ angelegt.`, 'OK', { duration: 3000 });
            this.selectTeam(saved.id);
          },
          error: (err: HttpErrorResponse) => this.snackBar.open(
            ResourceTeamManagementStore.errorText(err, 'Das Team konnte nicht gespeichert werden.'), 'OK', { duration: 5000 })
        });
      });
  }

  onTeamDeleted(): void {
    this.selectedTeamId = this.defaultTeamId();
    this.syncUrl();
  }
}
